// engagement-components/ProfileCompletionBuilder.jsx
// Dedicated builder for "Complete Your Profile" campaigns & journeys
// Handles: which fields to target, incentive copy, channel fallback, audience preview

import React, { useState, useEffect } from "react";
import {
  Row, Col, Card, Form, Button, Badge, Alert, Spinner,
  ListGroup, ProgressBar, Modal, Table,
} from "react-bootstrap";
import {
  FaUserEdit, FaWhatsapp, FaSms, FaEnvelope, FaLink,
  FaCheckCircle, FaTimesCircle, FaSave, FaRocket, FaEye,
  FaGift, FaBell,
} from "react-icons/fa";
import axios from "axios";
import { apiUrl, authHeaders, SAMPLE } from "./config";
import { RightModal, RSelect, useNotify, DownloadMenu } from "./shared";
import { PROFILE_FIELDS, applyFilters, profileCompleteness, emptyFilters } from "./AudienceBuilder";
import ChannelFallback, { DEFAULT_FALLBACK } from "./ChannelFallback";

// ─── Pre-built message templates per missing field ────────────────────────────
const FIELD_MESSAGES = {
  anniversary: {
    WhatsApp: "Hi {{guest_name}}! 🎉 We'd love to celebrate your anniversary with you at {{hotel_name}}. Share your anniversary date and get 10% off your next celebration stay → {{link}}",
    SMS:      "Hi {{guest_name}}, share your anniversary date with {{hotel_name}} & get 10% off your next stay: {{link}}",
    Email:    "Dear {{guest_name}},\n\nWe'd love to make your anniversary extra special at {{hotel_name}}.\n\nShare your anniversary date and enjoy 10% off your next stay:\n{{link}}\n\nWarm regards,\nThe Team",
  },
  dob: {
    WhatsApp: "Hi {{guest_name}}! 🎂 We want to send you birthday surprises! Share your birthday and unlock exclusive offers at {{hotel_name}} → {{link}}",
    SMS:      "Hi {{guest_name}}, share your birthday with {{hotel_name}} for exclusive offers: {{link}}",
    Email:    "Dear {{guest_name}},\n\nWe'd love to celebrate your birthday with you at {{hotel_name}}!\n\nShare your birthday date to unlock exclusive birthday offers:\n{{link}}",
  },
  email: {
    WhatsApp: "Hi {{guest_name}}! Stay updated with exclusive offers from {{hotel_name}}. Share your email and get 10% off your next booking → {{link}}",
    SMS:      "Hi {{guest_name}}, share your email with {{hotel_name}} & get 10% off: {{link}}",
    Email:    null, // can't email if no email
  },
  phone: {
    WhatsApp: null, // can't WhatsApp if no phone
    SMS:      null, // can't SMS if no phone
    Email:    "Dear {{guest_name}},\n\nShare your mobile number with {{hotel_name}} and get priority booking alerts + 10% off your next stay:\n{{link}}",
  },
  city: {
    WhatsApp: "Hi {{guest_name}}! Tell us which city you're from and we'll send you personalised offers from {{hotel_name}} → {{link}}",
    SMS:      "Hi {{guest_name}}, tell {{hotel_name}} your city for personalised offers: {{link}}",
    Email:    "Dear {{guest_name}},\n\nHelp us personalise your experience at {{hotel_name}}.\n\nShare your city and receive offers tailored just for you:\n{{link}}",
  },
};

// ─── Profile Completeness Stats Block ────────────────────────────────────────
const CompletenessStats = ({ guests }) => {
  const stats = PROFILE_FIELDS.map((f) => ({
    ...f,
    missing: guests.filter((g) => f.check(g)).length,
    pct:     guests.length > 0 ? Math.round((guests.filter((g) => f.check(g)).length / guests.length) * 100) : 0,
  }));

  return (
    <div>
      {stats.map((s) => (
        <div key={s.key} className="mb-3">
          <div className="d-flex justify-content-between align-items-center mb-1">
            <span className="small fw-semibold">
              {s.icon} {s.label}
            </span>
            <span className="small">
              <strong className={s.missing > 0 ? "text-warning" : "text-success"}>
                {s.missing} missing
              </strong>
              <span className="text-muted ms-1">/ {guests.length} guests</span>
            </span>
          </div>
          <ProgressBar
            now={100 - s.pct}
            variant={s.pct > 50 ? "danger" : s.pct > 20 ? "warning" : "success"}
            style={{ height: 7, borderRadius: 4 }}
          />
          <div className="text-muted" style={{ fontSize: "0.7rem" }}>
            {s.pct}% of guests missing this field
          </div>
        </div>
      ))}
    </div>
  );
};

// ─── Message Preview ──────────────────────────────────────────────────────────
const MsgPreview = ({ channel, content }) => {
  const rendered = (content || "")
    .replace("{{guest_name}}", "Rahul Sharma")
    .replace("{{hotel_name}}", "Grand Hotel")
    .replace("{{link}}", "hotel.co/complete-profile?token=xxx");

  if (channel === "WhatsApp") return (
    <div className="p-3 rounded" style={{ background: "#e5ddd5" }}>
      <div className="ms-auto p-3" style={{
        background: "#dcf8c6", maxWidth: "90%",
        fontSize: "0.82rem", lineHeight: 1.6,
        borderRadius: "12px 0 12px 12px", whiteSpace: "pre-wrap",
      }}>
        {rendered || <em className="text-muted">Message preview...</em>}
      </div>
    </div>
  );
  if (channel === "SMS") return (
    <div className="p-3 rounded border" style={{ background: "#f8f9fa", fontSize: "0.82rem", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
      {rendered || <em className="text-muted">SMS preview...</em>}
    </div>
  );
  return (
    <div className="border rounded overflow-hidden">
      <div className="bg-light px-3 py-2 border-bottom" style={{ fontSize: "0.72rem", color: "#6c757d" }}>From: hotel@netshine.com</div>
      <div className="p-3" style={{ fontSize: "0.82rem", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
        {rendered || <em className="text-muted">Email preview...</em>}
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const ProfileCompletionBuilder = ({ onSaved, onNavigate }) => {
  // Which fields to target
  const [targetFields,  setTargetFields]  = useState(["anniversary", "dob"]);
  const [incentive,     setIncentive]     = useState("10% off your next stay");
  const [regPageUrl,    setRegPageUrl]    = useState("");
  const [channelChain,  setChannelChain]  = useState([...DEFAULT_FALLBACK]);

  // Per-field messages (auto-populated, editable)
  const [messages,      setMessages]      = useState(() => {
    const m = {};
    PROFILE_FIELDS.forEach((f) => {
      m[f.key] = {
        WhatsApp: FIELD_MESSAGES[f.key]?.WhatsApp || "",
        SMS:      FIELD_MESSAGES[f.key]?.SMS      || "",
        Email:    FIELD_MESSAGES[f.key]?.Email    || "",
      };
    });
    return m;
  });

  // Data
  const [allGuests, setAllGuests] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState("");
  const [success,   setSuccess]   = useState("");
  const [previewField, setPreviewField] = useState(null);
  const [activePreviewChannel, setActivePreviewChannel] = useState("WhatsApp");

  useEffect(() => { fetchGuests(); }, []);

  const fetchGuests = async () => {
    try {
      const res = await axios.get(`${apiUrl}/feedback/guests`, authHeaders());
      // API returns { guests: [], total: N } — extract the array
      const raw = Array.isArray(res.data) ? res.data : (res.data?.guests || []);
      setAllGuests(raw.map((g) => ({ ...g, id: g.guestid || g.id })));
    } catch {
      setAllGuests([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleField = (key) =>
    setTargetFields((p) => p.includes(key) ? p.filter((k) => k !== key) : [...p, key]);

  const updateMsg = (fieldKey, channel, val) =>
    setMessages((p) => ({ ...p, [fieldKey]: { ...p[fieldKey], [channel]: val } }));

  // Compute audience: guests missing ANY of the target fields
  const targetedGuests = allGuests.filter((g) =>
    targetFields.some((fk) => {
      const def = PROFILE_FIELDS.find((f) => f.key === fk);
      return def?.check(g);
    })
  );

  // Per-field audience counts
  const fieldCounts = PROFILE_FIELDS.reduce((acc, f) => ({
    ...acc,
    [f.key]: allGuests.filter((g) => f.check(g)).length,
  }), {});

  // Save as campaign
  const saveAsCampaign = async () => {
    if (targetFields.length === 0) { setError("Select at least one field to target."); return; }
    if (targetedGuests.length === 0) { setError("No guests match — all profiles are complete!"); return; }
    setSaving(true);
    setError("");
    const payload = {
      name:          `Profile Completion — ${targetFields.map((k) => PROFILE_FIELDS.find((f) => f.key === k)?.label).join(", ")}`,
      type:          "profile_completion",
      targetFields,
      incentive,
      regPageUrl,
      channelChain,
      messages,
      audienceCount: targetedGuests.length,
      filters:       {
        ...emptyFilters(),
        missingFields: targetFields,
      },
      status:        "Draft",
    };
    try {
      await axios.post(`${apiUrl}/feedback/campaigns`, payload, authHeaders());
      setSuccess("Campaign created! Go to Campaigns to review and launch.");
    } catch {
      setSuccess("Campaign created (demo mode). Check Campaigns tab.");
    } finally {
      setSaving(false);
      setTimeout(() => { if (onNavigate) onNavigate("campaigns"); }, 1800);
    }
  };

  // Save as journey (trigger-based, fires automatically)
  const saveAsJourney = async () => {
    if (targetFields.length === 0) { setError("Select at least one field to target."); return; }
    setSaving(true);
    setError("");
    const payload = {
      name:    `Auto: Complete Your Profile`,
      trigger: "Profile Incomplete",
      triggerConditions: { missingFields: targetFields },
      channelChain,
      messages,
      incentive,
      regPageUrl,
      status:  "Draft",
      steps_data: channelChain.filter((c) => c.enabled).map((c, i) => ({
        id:            `pcs_${i}`,
        offsetDays:    0,
        executionTime: "10:00",
        channel:       c.channel,
        message:       messages[targetFields[0]]?.[c.channel] || "",
        isFallback:    i > 0,
      })),
    };
    try {
      await axios.post(`${apiUrl}/feedback/journeys`, payload, authHeaders());
      setSuccess("Journey created! Go to Journeys to activate.");
    } catch {
      setSuccess("Journey created (demo mode). Check Journeys tab.");
    } finally {
      setSaving(false);
      setTimeout(() => { if (onNavigate) onNavigate("journeys"); }, 1800);
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="page-header mb-4">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h3 className="fw-bold text-dark mb-0">
              <FaUserEdit className="text-warning me-2" />Profile Completion Campaign
            </h3>
            <p className="text-muted mb-0 mt-1">
              Target guests with incomplete profiles · Send incentive · Collect missing data
            </p>
          </div>
          <div className="d-flex gap-2">
            <Button variant="outline-primary" onClick={saveAsJourney} disabled={saving || targetFields.length === 0}>
              {saving ? <Spinner size="sm" animation="border" className="me-1" /> : <FaBell className="me-1" />}
              Save as Journey (auto-trigger)
            </Button>
            <Button variant="primary" onClick={saveAsCampaign} disabled={saving || targetedGuests.length === 0} className="px-4">
              {saving ? <Spinner size="sm" animation="border" className="me-1" /> : <FaRocket className="me-1" />}
              Create Campaign ({targetedGuests.length} guests)
            </Button>
          </div>
        </div>
      </div>

      {error   && <Alert variant="danger"  dismissible onClose={() => setError("")}>{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      <Row className="g-3">

        {/* ── LEFT COLUMN ─────────────────────────────────────────────────── */}
        <Col lg={7}>

          {/* Step 1: Target fields */}
          <Card className="shadow-sm border-0 mb-3">
            <Card.Header className="bg-white border-bottom py-3">
              <h6 className="fw-bold mb-0"><span className="badge bg-primary me-2">1</span>Target Missing Fields</h6>
            </Card.Header>
            <Card.Body className="p-4">
              <p className="text-muted small mb-3">
                Select which missing fields should trigger this campaign. Guests missing <strong>any</strong> of the selected fields will receive a message.
              </p>
              <div className="d-flex flex-wrap gap-2 mb-3">
                {PROFILE_FIELDS.map((f) => (
                  <Button key={f.key} size="sm"
                    variant={targetFields.includes(f.key) ? "warning" : "outline-secondary"}
                    className="rounded-pill"
                    onClick={() => toggleField(f.key)}
                    aria-pressed={targetFields.includes(f.key)}>
                    {f.icon} {f.label}
                    {fieldCounts[f.key] > 0 && (
                      <Badge bg={targetFields.includes(f.key) ? "light" : "secondary"}
                        text={targetFields.includes(f.key) ? "dark" : "white"}
                        className="ms-2" style={{ fontSize: "0.65rem" }}>
                        {fieldCounts[f.key]}
                      </Badge>
                    )}
                  </Button>
                ))}
              </div>

              {targetFields.length > 0 && (
                <div className="p-3 rounded" style={{ background: "#fffbeb", border: "1px solid #fde68a" }}>
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <span className="fw-semibold small">
                      <FaUserEdit className="me-1 text-warning" />
                      {targetedGuests.length} guests with incomplete profiles
                    </span>
                    <span className="text-muted small">
                      {allGuests.length - targetedGuests.length} / {allGuests.length} profiles complete
                    </span>
                  </div>
                  <ProgressBar
                    now={allGuests.length > 0 ? ((allGuests.length - targetedGuests.length) / allGuests.length) * 100 : 0}
                    variant="success"
                    style={{ height: 8, borderRadius: 4 }}
                  />
                </div>
              )}
            </Card.Body>
          </Card>

          {/* Step 2: Incentive & link */}
          <Card className="shadow-sm border-0 mb-3">
            <Card.Header className="bg-white border-bottom py-3">
              <h6 className="fw-bold mb-0"><span className="badge bg-success me-2">2</span>Incentive & Registration Link</h6>
            </Card.Header>
            <Card.Body className="p-4">
              <Row className="g-3">
                <Col md={6}>
                  <Form.Label className="fw-semibold">
                    <FaGift className="me-1 text-success" />Incentive Offer
                  </Form.Label>
                  <Form.Control
                    value={incentive}
                    onChange={(e) => setIncentive(e.target.value)}
                    placeholder="e.g. 10% off your next stay"
                  />
                  <Form.Text className="text-muted">Used in auto-generated message copy</Form.Text>
                </Col>
                <Col md={6}>
                  <Form.Label className="fw-semibold">
                    <FaLink className="me-1 text-primary" />Profile Completion Link
                  </Form.Label>
                  <Form.Control
                    value={regPageUrl}
                    onChange={(e) => setRegPageUrl(e.target.value)}
                    placeholder="/guest-register?org=xxx or /o/complete-profile"
                  />
                  <Form.Text className="text-muted">
                    Use your QR registration page or a custom form. <code>{"{{link}}"}</code> in messages will resolve to this.
                  </Form.Text>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Step 3: Per-field messages */}
          <Card className="shadow-sm border-0 mb-3">
            <Card.Header className="bg-white border-bottom py-3">
              <h6 className="fw-bold mb-0"><span className="badge bg-info me-2">3</span>Message per Field</h6>
            </Card.Header>
            <Card.Body className="p-4">
              <p className="text-muted small mb-3">
                Each field gets its own personalised message. The system picks the right one based on what's missing for each guest.
              </p>
              {targetFields.map((fk) => {
                const def = PROFILE_FIELDS.find((f) => f.key === fk);
                return (
                  <div key={fk} className="mb-4">
                    <div className="fw-semibold mb-2">
                      {def.icon} {def.label}
                      <Badge bg="warning" text="dark" className="ms-2" style={{ fontSize: "0.65rem" }}>
                        {fieldCounts[fk]} guests
                      </Badge>
                      <Button variant="link" size="sm" className="ms-2 p-0 text-primary"
                        style={{ fontSize: "0.78rem" }}
                        onClick={() => { setPreviewField(fk); setActivePreviewChannel("WhatsApp"); }}>
                        <FaEye size={11} className="me-1" />Preview
                      </Button>
                    </div>

                    {/* Tab-style channel switcher */}
                    <div className="d-flex gap-1 mb-2">
                      {["WhatsApp", "SMS", "Email"].map((ch) => (
                        <Button key={ch} size="sm"
                          variant={messages[fk]?.[ch] ? "outline-primary" : "outline-secondary"}
                          className="rounded-pill py-0 px-2"
                          style={{ fontSize: "0.72rem" }}>
                          {ch === "WhatsApp" && <FaWhatsapp className="me-1" />}
                          {ch === "SMS"      && <FaSms className="me-1" />}
                          {ch === "Email"    && <FaEnvelope className="me-1" />}
                          {ch}
                          {!messages[fk]?.[ch] && (
                            <Badge bg="danger" className="ms-1" style={{ fontSize: "0.6rem" }}>empty</Badge>
                          )}
                        </Button>
                      ))}
                    </div>

                    {/* WhatsApp message */}
                    <Form.Control as="textarea" rows={2} size="sm"
                      value={messages[fk]?.WhatsApp || ""}
                      onChange={(e) => updateMsg(fk, "WhatsApp", e.target.value)}
                      placeholder="WhatsApp message..."
                      className="mb-1"
                      style={{ resize: "none", fontSize: "0.82rem" }}
                    />

                    {/* SMS */}
                    <Form.Control size="sm"
                      value={messages[fk]?.SMS || ""}
                      onChange={(e) => updateMsg(fk, "SMS", e.target.value)}
                      placeholder="SMS message (shorter)..."
                      className="mb-1"
                      style={{ fontSize: "0.82rem" }}
                    />

                    {/* Email */}
                    <Form.Control as="textarea" rows={3} size="sm"
                      value={messages[fk]?.Email || ""}
                      onChange={(e) => updateMsg(fk, "Email", e.target.value)}
                      placeholder="Email body..."
                      style={{ resize: "none", fontSize: "0.82rem" }}
                    />
                  </div>
                );
              })}

              {targetFields.length === 0 && (
                <div className="text-center text-muted py-3">
                  Select target fields above to configure messages
                </div>
              )}
            </Card.Body>
          </Card>

          {/* Step 4: Channel fallback */}
          <div className="mb-3">
            <div className="d-flex align-items-center mb-2">
              <span className="badge bg-warning text-dark me-2">4</span>
              <h6 className="fw-bold mb-0">Channel Priority & Fallback</h6>
            </div>
            <ChannelFallback chain={channelChain} setChain={setChannelChain} mode="full" />
          </div>
        </Col>

        {/* ── RIGHT COLUMN ────────────────────────────────────────────────── */}
        <Col lg={5}>

          {/* Profile completeness stats */}
          <Card className="shadow-sm border-0 mb-3" style={{ position: "sticky", top: 80 }}>
            <Card.Header className="bg-white border-bottom py-3">
              <h6 className="fw-bold mb-0">Profile Health — All Guests</h6>
            </Card.Header>
            <Card.Body className="p-4">
              {loading
                ? <Spinner animation="border" variant="primary" size="sm" />
                : <CompletenessStats guests={allGuests} />}

              <div className="mt-3 pt-3 border-top">
                <div className="fw-semibold small mb-2">Targeted audience summary</div>
                {targetFields.map((fk) => {
                  const def = PROFILE_FIELDS.find((f) => f.key === fk);
                  const cnt = fieldCounts[fk];
                  return (
                    <div key={fk} className="d-flex justify-content-between align-items-center mb-1"
                      style={{ fontSize: "0.82rem" }}>
                      <span>{def?.icon} {def?.label}</span>
                      <Badge bg={cnt > 0 ? "warning" : "success"} text={cnt > 0 ? "dark" : "white"}>
                        {cnt > 0 ? `${cnt} will receive` : "All complete ✓"}
                      </Badge>
                    </div>
                  );
                })}
                {targetFields.length > 0 && (
                  <div className="mt-2 p-2 rounded text-center fw-bold"
                    style={{ background: "#f0fdf4", color: "#166534", fontSize: "0.9rem", border: "1px solid #bbf7d0" }}>
                    <FaRocket className="me-1" />
                    {targetedGuests.length} guests will receive this campaign
                  </div>
                )}
              </div>
            </Card.Body>
          </Card>

          {/* Guest list preview */}
          {targetedGuests.length > 0 && (
            <Card className="shadow-sm border-0">
              <Card.Header className="bg-white border-bottom py-3">
                <h6 className="fw-bold mb-0">
                  <FaEye className="text-muted me-2" />Sample audience
                </h6>
              </Card.Header>
              <Card.Body className="p-0">
                <Table hover size="sm" className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th className="ps-3 py-2 border-0 fw-semibold text-muted" style={{ fontSize: "0.7rem" }}>Guest</th>
                      <th className="py-2 border-0 fw-semibold text-muted" style={{ fontSize: "0.7rem" }}>Missing</th>
                      <th className="py-2 border-0 fw-semibold text-muted" style={{ fontSize: "0.7rem" }}>Channel</th>
                    </tr>
                  </thead>
                  <tbody>
                    {targetedGuests.slice(0, 8).map((g) => {
                      const missing = PROFILE_FIELDS.filter((f) => targetFields.includes(f.key) && f.check(g));
                      const ch = g.phone ? (channelChain.find((c) => c.enabled)?.channel || "WhatsApp") : "Email";
                      return (
                        <tr key={g.id}>
                          <td className="ps-3" style={{ fontSize: "0.8rem" }}>
                            <div className="fw-semibold">{g.name}</div>
                            <div className="text-muted" style={{ fontSize: "0.7rem" }}>{g.phone || g.email}</div>
                          </td>
                          <td style={{ fontSize: "0.75rem" }}>
                            {missing.map((f) => (
                              <span key={f.key} className="me-1">{f.icon}</span>
                            ))}
                          </td>
                          <td>
                            <Badge bg={ch === "WhatsApp" ? "success" : ch === "SMS" ? "secondary" : "primary"}
                              style={{ fontSize: "0.65rem" }}>
                              {ch}
                            </Badge>
                          </td>
                        </tr>
                      );
                    })}
                    {targetedGuests.length > 8 && (
                      <tr>
                        <td colSpan={3} className="text-center text-muted ps-3 py-2" style={{ fontSize: "0.75rem" }}>
                          +{targetedGuests.length - 8} more guests
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          )}
        </Col>
      </Row>

      {/* Message Preview Modal */}
      <Modal show={!!previewField} onHide={() => setPreviewField(null)} centered aria-label="Message Preview">
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">
            {PROFILE_FIELDS.find((f) => f.key === previewField)?.icon}{" "}
            {PROFILE_FIELDS.find((f) => f.key === previewField)?.label} — Message Preview
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <div className="d-flex gap-2 mb-3">
            {["WhatsApp","SMS","Email"].map((ch) => (
              <Button key={ch} size="sm"
                variant={activePreviewChannel === ch ? "primary" : "outline-secondary"}
                className="rounded-pill"
                onClick={() => setActivePreviewChannel(ch)}>
                {ch}
              </Button>
            ))}
          </div>
          {previewField && (
            <MsgPreview
              channel={activePreviewChannel}
              content={messages[previewField]?.[activePreviewChannel]}
            />
          )}
          <div className="mt-3 p-2 rounded bg-light border" style={{ fontSize: "0.75rem" }}>
            <strong>Variables:</strong><br />
            <code>{"{{guest_name}}"}</code> → Guest name &nbsp;
            <code>{"{{hotel_name}}"}</code> → Hotel name &nbsp;
            <code>{"{{link}}"}</code> → Profile completion link
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setPreviewField(null)}>Close</Button>
        </Modal.Footer>
      </Modal>

      <style>{`
        .card { border-radius: 12px !important; }
        .card-header { border-radius: 12px 12px 0 0 !important; }
        .page-header { margin-bottom: 1.5rem; }
      `}</style>
    </div>
  );
};

export default ProfileCompletionBuilder;
